import { formatAbbreviatedValueWithMetricUnit } from "@/utils/number";
import { Button, InputNumber, Slider } from "antd";
import { useState } from "react";
import { type Domain, normaliseManualRange } from "./common/dumbbellChartGeometry";

/** Handles land on 1/200th of the bounds; the range can never be narrower than 1/100th of them. */
const STEPS_ACROSS_BOUNDS = 200;
const MIN_SPAN_FRACTION = 0.01;
const isSameRange = (a: Domain, b: Domain) => a[0] === b[0] && a[1] === b[1];

/**
 * Narrows the axis so a handful of outliers stop flattening the rest of the portfolio.
 *
 * Edits are held as a draft and only reach the chart on Apply — dragging a handle would otherwise
 * rescale and redraw every row on each frame.
 *
 * `bounds` must span every asset with data, before exclusions and before the search. Bounds that
 * tracked the visible domain would need the active range clamped to them, and that clamp would move
 * the domain, which would move the bounds again.
 */
export const DumbellRangeFilter = ({
  bounds,
  value,
  isManual,
  unit,
  onChange,
  onReset
}: {
  bounds: Domain;
  value: Domain;
  isManual: boolean;
  unit: "%" | "$";
  onChange: (range: Domain) => void;
  onReset: () => void;
}) => {
  const [boundsMin, boundsMax] = bounds;
  const boundsSpan = boundsMax - boundsMin;
  const step = boundsSpan > 0 ? boundsSpan / STEPS_ACROSS_BOUNDS : 1;
  const minSpan = boundsSpan * MIN_SPAN_FRACTION;
  const [draft, setDraft] = useState<Domain>(value);

  // Follows the axis when something outside resets it — a metric switch, or Reset.
  const [lastValue, setLastValue] = useState<Domain>(value);
  if (!isSameRange(lastValue, value)) {
    setLastValue(value);
    setDraft(value);
  }
  const format = (v: number) => formatAbbreviatedValueWithMetricUnit(v, unit);
  const limits = {
    min: boundsMin,
    max: boundsMax,
    step
  };
  return <div className="flex w-64 flex-col gap-1" data-sentry-component="DumbellRangeFilter" data-sentry-source-file="DumbellRangeFilter.tsx">
      <Slider range {...limits} value={draft} onChange={next => setDraft(next as Domain)} tooltip={{
      formatter: v => format(v ?? 0)
    }} ariaLabelForHandle={["Axis minimum", "Axis maximum"]} ariaValueTextFormatterForHandle={format} data-sentry-element="Slider" data-sentry-source-file="DumbellRangeFilter.tsx" />

      <div className="flex items-center gap-2">
        <InputNumber {...limits} value={draft[0]} onChange={next => setDraft([next ?? boundsMin, draft[1]])} size="small" className="flex-1" aria-label="Axis minimum" data-sentry-element="InputNumber" data-sentry-source-file="DumbellRangeFilter.tsx" />
        <span className="text-xs text-gray-400">to</span>
        <InputNumber {...limits} value={draft[1]} onChange={next => setDraft([draft[0], next ?? boundsMax])} size="small" className="flex-1" aria-label="Axis maximum" data-sentry-element="InputNumber" data-sentry-source-file="DumbellRangeFilter.tsx" />
      </div>

      <div className="mt-1 flex items-center justify-between">
        <Button variant="filled" size="small" disabled={!isManual} onClick={onReset} data-sentry-element="Button" data-sentry-source-file="DumbellRangeFilter.tsx">
          Reset
        </Button>
        <Button type="primary" size="small" disabled={isSameRange(draft, value)} onClick={() => onChange(normaliseManualRange(draft, bounds, minSpan))} data-sentry-element="Button" data-sentry-source-file="DumbellRangeFilter.tsx">
          Apply
        </Button>
      </div>
    </div>;
};